// frontend/app/components/lessonPlans/LessonResultRow.js
//
// One lesson, as a row.
//
// WHY THIS IS ITS OWN FILE. It was written inside `LessonFinder`, where it was
// the only way a lesson was ever drawn. The browse tree is a second way to
// arrive at the same lesson, and a lesson found by browsing that looked
// different from the same lesson found by typing would read as a different
// thing. Both import this; neither owns it, so neither can drift.
//
// A shared module rather than an export from the finder, because the tree is
// rendered *by* the finder — importing the row back out of it would be a cycle.

import React, { createContext, useContext } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { SARAS_BLUE } from "./lessonPlanTheme";

/**
 * Bridges "Make my copy" from the launcher down to a row without either of
 * this row's two real parents (`LessonFinder`, `LessonBrowseTree`) knowing
 * fork exists.
 *
 * WHY A CONTEXT AND NOT A THIRD PROP THREADED THROUGH THOSE TWO FILES. They
 * already forward `onOpen`/`onEdit` explicitly, and adding `onFork` the same
 * way is the more obvious shape — but this row is shared by both, and this
 * package's scope is this file and `LessonPlanLauncher`, not the finder or
 * the browse tree. `LessonPlanLauncher` wraps `LessonFinder` (which itself
 * renders `LessonBrowseTree`) in `LessonForkProvider`, so every row under it
 * picks up fork support with no change to either intermediate file.
 *
 * `onFork`/`forking`/`audience` are still ordinary props here first — a test
 * can render `ResultRow` directly with them, exactly as the finder's own
 * `onEdit` prop works today. The context is read only as a fallback when a
 * prop is not explicitly given, so a direct caller's `undefined` always wins.
 */
const LessonForkContext = createContext(null);
export const LessonForkProvider = LessonForkContext.Provider;

export const DONE_HEX = "#10b981";

// Difficulty used to pick this row's accent out of a three-colour map; the
// SME review retired the difficulty pill (it read as "three versions of the
// same lesson," which our 37 lessons are not — see lessonMatch.js for why the
// ranking weight itself stays). Every row now wears the one Saras blue.
export const accentFor = () => SARAS_BLUE.light;

/** See LessonPlanLauncher: a translator gets one whole clause, not fragments. */
export const fill = (template, values) =>
  String(template).replace(/\{(\w+)\}/g, (token, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token
  );

export const breadcrumbOf = (lesson) =>
  [lesson?.chapter_name, lesson?.topic_name].filter(Boolean).join("  ›  ");

/**
 * Everything needed to choose between two lessons on the same topic — what it
 * is, where it sits, how long it runs, how hard it is, and the first thing the
 * class will be able to do afterwards.
 */
const ResultRow = ({
  lesson,
  progress,
  terms = [],
  reason = null,
  // Whether `reason` is Saras's sentence or a deterministic ranker's. Only the
  // first is painted in the AI accent — see the finder's group heading.
  reasonIsAi = false,
  active = false,
  colors,
  isDarkMode,
  onOpen,
  onEdit,
  // `(lesson) => void`. See LessonForkContext above for why a direct prop and
  // a context both work — a caller that passes nothing here falls back to
  // whatever LessonPlanLauncher provided, or to nothing at all.
  onFork,
  // The id of the lesson currently being copied, or nothing. Compared to this
  // row's own id, not a boolean, so one in-flight copy disables only its own
  // row and every other "Make my copy" in the list stays pressable.
  forking,
  // "teacher" | "student". A student has nothing to copy — lessons are handed
  // to them, not authored — so the button never appears there even if a
  // caller passed `onFork` by mistake.
  audience,
  t,
}) => {
  const translate = t || ((value) => value);
  const forkContext = useContext(LessonForkContext);
  const resolvedOnFork = onFork !== undefined ? onFork : forkContext?.onFork;
  const resolvedForking = forking !== undefined ? forking : forkContext?.forking;
  const resolvedAudience = audience !== undefined ? audience : forkContext?.audience || "teacher";
  const accent = accentFor();
  const objectives = Array.isArray(lesson.objectives) ? lesson.objectives : [];
  const isComplete = Boolean(progress?.completed);
  const total = Number(lesson.step_count) || 0;
  const breadcrumb = breadcrumbOf(lesson);

  // Per lesson, not per role. A teacher may edit what they created and not the
  // shared library, so a single "is a teacher" boolean cannot answer this — two
  // lessons side by side in the same list get different answers.
  //
  // `can_edit` is computed server-side from the same predicate the route
  // enforces, so the button and the 403 cannot disagree. Explicitly `=== true`:
  // an older server, or a list cached from before the deploy, sends nothing
  // here and must hide the button rather than offer one that fails at save —
  // after the work is done, which is the expensive place to find out.
  const canEdit = Boolean(onEdit) && lesson.can_edit === true;

  // The secondary action for everything a teacher cannot edit: never both
  // buttons on one row, and never on the student dashboard, which has nothing
  // to author from.
  const canFork =
    !canEdit && typeof resolvedOnFork === "function" && resolvedAudience === "teacher";
  const isForking = canFork && resolvedForking === lesson.id;

  // Who wrote this, in the order a teacher would ask it: their own work reads
  // "by you" even though `author_name` would also be their own name; a
  // colleague's gets named; the seeded library gets the library's name. A
  // teacher-authored row with no name to show (a soft-deleted author, or a
  // list cached from before author_name shipped) says nothing rather than
  // showing "by" with nothing after it.
  const authorLine = lesson.is_mine
    ? translate("by you")
    : lesson.source === "teacher" && lesson.author_name
      ? fill(translate("by {author}"), { author: lesson.author_name })
      : lesson.source === "library"
        ? translate("Saras library")
        : null;

  // The lesson this one was copied from, if any. `author_name` on `based_on`
  // is optional even when the row's own author is known — the original's
  // author can be gone in a way this copy's author is not — so the two
  // templates are whole sentences, not one with a fragment spliced in; a
  // translator gets a complete clause either way.
  const basedOnLine = lesson.based_on
    ? lesson.based_on.author_name
      ? fill(translate('Based on "{title}" by {author}'), {
          title: lesson.based_on.title,
          author: lesson.based_on.author_name,
        })
      : fill(translate('Based on "{title}"'), { title: lesson.based_on.title })
    : null;

  const progressText = isComplete
    ? translate("Completed")
    : progress
      ? total
        ? fill(translate("Step {step} of {total}"), {
            step: Math.min((Number(progress.step_index) || 0) + 1, total),
            total,
          })
        : translate("In progress")
      : total
        ? fill(translate("{total} steps"), { total })
        : null;

  return (
    <TouchableOpacity
      testID={`lesson-finder-result-${lesson.id}`}
      onPress={() => onOpen(lesson)}
      accessibilityRole="button"
      accessibilityLabel={`${translate("Lesson")} ${lesson.lesson_no}: ${lesson.title}`}
      style={[
        styles.row,
        {
          backgroundColor: active ? colors.accentGlow : colors.cardBg,
          borderColor: active ? colors.accentPrimary : colors.border,
        },
      ]}
    >
      <View style={[styles.rowBadge, { backgroundColor: `${accent}1F` }]}>
        <Text style={[styles.rowBadgeLabel, { color: accent }]}>
          {translate("Lesson")}
        </Text>
        <Text style={[styles.rowBadgeNumber, { color: accent }]}>
          {lesson.lesson_no}
        </Text>
      </View>

      <View style={styles.rowBody}>
        <Text
          style={[styles.rowTitle, { color: colors.textPrimary }]}
          numberOfLines={2}
        >
          {lesson.title}
        </Text>
        {breadcrumb ? (
          <Text style={[styles.rowTopic, { color: colors.textMuted }]} numberOfLines={1}>
            {breadcrumb}
          </Text>
        ) : null}

        {authorLine ? (
          <Text style={[styles.rowAuthor, { color: colors.textMuted }]} numberOfLines={1}>
            {authorLine}
          </Text>
        ) : null}
        {basedOnLine ? (
          <Text
            style={[styles.rowAuthor, styles.rowBasedOn, { color: colors.textMuted }]}
            numberOfLines={1}
          >
            {basedOnLine}
          </Text>
        ) : null}

        {objectives.length > 0 ? (
          <Text
            style={[styles.rowObjective, { color: colors.textSecondary }]}
            numberOfLines={2}
          >
            {objectives[0]}
          </Text>
        ) : null}

        {/* Why the server returned this one. Set on server suggestions only —
            a locally ranked row explains itself with the teacher's own words
            below. Whether this sentence was written by Saras or by the
            deterministic ranker behind it depends on `source`, which is why the
            accent is conditional and the group heading above says which. */}
        {reason ? (
          <Text
            style={[
              styles.rowReason,
              { color: reasonIsAi ? colors.accentPrimary : colors.textSecondary },
            ]}
            numberOfLines={2}
          >
            {reason}
          </Text>
        ) : null}

        <View style={styles.rowMeta}>
          {lesson.status === "draft" ? (
            <View style={[styles.statusBadge, { backgroundColor: "#f59e0b22" }]}>
              <MaterialCommunityIcons name="pencil-outline" size={12} color="#f59e0b" />
              <Text style={[styles.statusText, { color: "#f59e0b" }]}>
                {translate("Draft")}
              </Text>
            </View>
          ) : null}
          {lesson.duration_minutes ? (
            <View style={styles.metaItem}>
              <MaterialCommunityIcons
                name="clock-outline"
                size={13}
                color={colors.textMuted}
              />
              <Text style={[styles.metaText, { color: colors.textMuted }]}>
                {lesson.duration_minutes} {translate("min")}
              </Text>
            </View>
          ) : null}
          {objectives.length > 0 ? (
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="target" size={13} color={colors.textMuted} />
              <Text style={[styles.metaText, { color: colors.textMuted }]}>
                {objectives.length} {translate("objectives")}
              </Text>
            </View>
          ) : null}
          {progressText ? (
            <Text
              style={[
                styles.metaText,
                { color: isComplete ? DONE_HEX : colors.textMuted },
              ]}
            >
              {progressText}
            </Text>
          ) : null}
        </View>

        {/* Why this row is here at all. A ranked list with no visible reason is
            indistinguishable from a guess. Empty when the row was reached by
            browsing — the tree already said why it is here. */}
        {terms.length > 0 ? (
          <Text style={[styles.rowTerms, { color: colors.textMuted }]} numberOfLines={1}>
            {fill(translate("Matched: {terms}"), { terms: terms.join(", ") })}
          </Text>
        ) : null}
      </View>

      <View style={styles.rowActions}>
        <Text
          style={[styles.rowCta, { color: isComplete ? DONE_HEX : accent }]}
          numberOfLines={1}
        >
          {isComplete
            ? translate("Review lesson")
            : progress
              ? translate("Resume lesson")
              : translate("Open lesson")}
        </Text>
        {canEdit ? (
          <TouchableOpacity
            onPress={() => onEdit(lesson)}
            accessibilityRole="button"
            accessibilityLabel={`${translate("Edit")} ${lesson.title}`}
            style={styles.editButton}
          >
            <MaterialCommunityIcons
              name="pencil-outline"
              size={14}
              color={colors.textMuted}
            />
            <Text style={[styles.editText, { color: colors.textMuted }]}>
              {translate("Edit")}
            </Text>
          </TouchableOpacity>
        ) : canFork ? (
          <TouchableOpacity
            testID={`lesson-finder-fork-${lesson.id}`}
            onPress={() => {
              if (!isForking) resolvedOnFork(lesson);
            }}
            disabled={isForking}
            accessibilityRole="button"
            accessibilityLabel={`${translate("Make my copy")} ${lesson.title}`}
            style={[styles.editButton, isForking && styles.editButtonDisabled]}
          >
            <MaterialCommunityIcons
              name="content-copy"
              size={13}
              color={colors.textMuted}
            />
            <Text style={[styles.editText, { color: colors.textMuted }]}>
              {isForking ? translate("Copying…") : translate("Make my copy")}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    gap: 12,
  },
  rowBadge: {
    borderRadius: 10,
    paddingHorizontal: 9,
    paddingVertical: 5,
    alignItems: "center",
    minWidth: 48,
  },
  rowBadgeLabel: {
    fontSize: 8,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  rowBadgeNumber: { fontSize: 16, fontWeight: "800", lineHeight: 20 },
  rowBody: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: 14, fontWeight: "700", lineHeight: 19 },
  rowTopic: { fontSize: 11, marginTop: 2 },
  rowAuthor: { fontSize: 11, fontWeight: "600", marginTop: 2 },
  rowBasedOn: { fontWeight: "400", fontStyle: "italic" },
  rowObjective: { fontSize: 12, lineHeight: 17, marginTop: 5 },
  rowReason: { fontSize: 12, lineHeight: 17, marginTop: 5, fontWeight: "600" },
  rowMeta: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 7,
  },
  rowTerms: { fontSize: 11, marginTop: 6, fontStyle: "italic" },
  rowActions: { alignItems: "flex-end", gap: 8 },
  rowCta: { fontSize: 12, fontWeight: "700" },

  metaItem: { flexDirection: "row", alignItems: "center", gap: 4 },
  metaText: { fontSize: 11, fontWeight: "600", marginLeft: 4 },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
    gap: 4,
  },
  statusText: { fontSize: 10, fontWeight: "800", marginLeft: 4 },
  editButton: { flexDirection: "row", alignItems: "center", gap: 4 },
  editButtonDisabled: { opacity: 0.6 },
  editText: { fontSize: 11, fontWeight: "600" },
});

export default ResultRow;
